import { useRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { useAddAssetsLiabilitiesImages } from "app/hooks/assetsLiabilities/useAddAssetsLiabilitiesImages";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import toast from "react-hot-toast";
import UploadIcon from "app/assets/icons/UploadIcon";
import DeleteIcon from "app/assets/icons/DeleteIcon";
import Loader from "app/components/common/Loader";
import pdf_icon from "app/assets/icons/pdf_icon.svg";
import { usePresignedUrls } from "app/hooks/assetsLiabilities/usePresignedUrls";
import { useS3Upload } from "app/hooks/assetsLiabilities/useS3Upload";
import { handleApiError } from "app/utils/handleApiError";

interface FormData {
  passportimage: File | null;
  visaImage: File | null;
}
interface Props {
  progressValue?: number;
}
const PartEighth: React.FC<Props> = ({ progressValue }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: getPresignedUrls } = usePresignedUrls();
  const { mutate: uploadS3 } = useS3Upload();
  const { mutate: uploadImages } = useAddAssetsLiabilitiesImages();

  const { visaAndPassportimages, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  );

  const frontFileInputRef = useRef<HTMLInputElement>(null);
  const backFileInputRef = useRef<HTMLInputElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<FormData>();
  const passportimage = watch("passportimage");
  const visaImage = watch("visaImage");

  const { applicantID, ...savedIds } = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );

  const onSubmit = async (data: FormData) => {
    if (!applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const { visaImage, passportimage } = data;

    if (!(visaImage instanceof File) && !(passportimage instanceof File)) {
      dispatch(nextStep());
      return;
    }
    const documentTypes: string[] = [];
    const metadata: Record<string, any> = {};

    if (visaImage instanceof File) documentTypes.push("visa");
    if (passportimage instanceof File) documentTypes.push("passport");

    if (!documentTypes.length) {
      dispatch(nextStep());
      return;
    }
    try {
      setIsLoading(true);
      getPresignedUrls(
        {
          url: `/document/${applicantID}/presigned-urls`,
          data: { documentTypes },
        },
        {
          onSuccess: async (presignedData) => {
            try {
              if (passportimage instanceof File) {
                const { url, key } = presignedData.passport || {};
                await uploadToS3(url, passportimage);
                metadata["passport"] = {
                  key,
                  fileName: passportimage.name,
                  mimeType: passportimage.type,
                };
              }

              if (visaImage instanceof File) {
                const { url, key } = presignedData.visa || {};
                await uploadToS3(url, visaImage);
                metadata["visa"] = {
                  key,
                  fileName: visaImage.name,
                  mimeType: visaImage.type,
                };
              }

              uploadImages(
                {
                  url: `/document/${applicantID}/visa-passport`,
                  data: metadata,
                },
                {
                  onSuccess: (data: any) => {
                    const images: { [key: string]: string } = {};
                    data.forEach((item: any) => {
                      if (item.type === "visa") {
                        images.visa = item.url;
                      } else if (item.type === "passport") {
                        images.passport = item.url;
                      }
                    });

                    dispatch(
                      addStepsData({
                        ...others,
                        visaAndPassportimages: {
                          visaImage: images.visa,
                          passportImage: images.passport,
                        },
                      })
                    );
                    dispatch(nextStep());
                    setIsLoading(false);
                  },
                  onError: (err) => {
                    setIsLoading(false);
                    toast.error("Saving metadata failed.");
                  },
                }
              );
            } catch (err) {
              setIsLoading(false);
              toast.error("S3 upload failed.");
            }
          },
        }
      );
    } catch (error: any) {
      setIsLoading(false);
      handleApiError(error);
    }
  };
  const uploadToS3 = (url: string, file: File) => {
    return new Promise<void>((resolve, reject) => {
      uploadS3(
        { url, data: file },
        {
          onSuccess: (status: any) => (status === 200 ? resolve() : reject()),
          onError: reject,
        }
      );
    });
  };

  const handleFileChooseClick = (type: "front" | "back") => {
    if (type === "front" && frontFileInputRef.current) {
      frontFileInputRef.current.click();
    } else if (type === "back" && backFileInputRef.current) {
      backFileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "front" | "back"
  ) => {
    const file = e.target.files?.[0] ?? null;
    const fieldName = fileType === "front" ? "passportimage" : "visaImage";
    clearErrors(fieldName);
    e.target.value = "";

    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError(fieldName, {
        type: "fileSize",
        message: "File size should not exceed 5MB",
      });
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setError(fieldName, {
        type: "fileType",
        message: "Invalid file type. Only JPG, PNG, or PDF allowed",
      });
      return;
    }
    // Only set value if validation passes
    setValue(fieldName, file);
    clearErrors(fieldName);
  };

  const handleDeleteImage = (type: "passport" | "visa") => {
    if (type === "passport") {
      dispatch(
        addStepsData({
          ...others,
          visaAndPassportimages: {
            ...visaAndPassportimages,
            passportImage: "",
          },
        })
      );
      setValue("passportimage", null);
      setpassportPreview(null);
    } else if (type === "visa") {
      setValue("visaImage", null);
      dispatch(
        addStepsData({
          ...others,
          visaAndPassportimages: {
            ...visaAndPassportimages,
            visaImage: "",
          },
        })
      );
      setvisaImagePreview(null);
    }
  };

  const [passportimagePreview, setpassportPreview] = useState<string | null>(
    null
  );
  useEffect(() => {
    if (passportimage instanceof File) {
      const objectUrl = URL.createObjectURL(passportimage);
      setpassportPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (visaAndPassportimages?.passportImage) {
      setpassportPreview(visaAndPassportimages?.passportImage?.toString());
    }
  }, [passportimage, visaAndPassportimages?.passportImage]);

  const [visaImageimagePreview, setvisaImagePreview] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (visaImage instanceof File) {
      const objectUrl = URL.createObjectURL(visaImage);
      setvisaImagePreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (visaAndPassportimages?.visaImage) {
      setvisaImagePreview(visaAndPassportimages?.visaImage?.toString());
    }
  }, [visaImage, visaAndPassportimages?.visaImage]);

  return (
    <>
      {isLoading && (
        <Loader text="Please wait while your documents are being uploaded..." />
      )}
      <ProgressBar progress={progressValue || 100} MB={7} />
      <div className="w-full sm:w-[80%] xl:w-[60%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Legal Documents" />
        <SubTitle title="Please upload your international passport & visa application" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left">
            <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-2">
              Upload passport & visa image
              <span className="text-danger">*</span>
            </h6>
            <div className="flex flex-col sm:flex-row gap-8 sm:gap-6">
              <div className="w-full sm:w-1/2">
                {passportimagePreview ? (
                  <div className="bg-[#EAECF0] p-5 w-full text-center rounded-[20px] relative h-[250px] flex justify-center items-center">
                    {passportimage &&
                    passportimage.type === "application/pdf" ? (
                      <div className="text-center">
                        <img
                          src={pdf_icon}
                          alt="pdf_icon "
                          className="rounded-[20px] text-center m-auto"
                        />
                        <p className="font-Mulish text-[#000] font-normal text-base">
                          {passportimage.name.length > 25
                            ? `${passportimage.name.slice(0, 25)}...`
                            : passportimage.name}
                        </p>
                      </div>
                    ) : (
                      <img
                        src={passportimagePreview}
                        alt="Uploaded Preview"
                        className="mx-auto  h-[220px]  rounded-[20px]"
                        onError={(e) => (e.currentTarget.src = pdf_icon)}
                      />
                    )}
                    <DeleteIcon
                      onClick={() => handleDeleteImage("passport")}
                      className="absolute cursor-pointer top-[10px] right-[10px]"
                    />
                  </div>
                ) : (
                  <div className="bg-[#EAECF0] p-5 w-full text-center rounded-[20px] relative h-[250px] flex justify-center items-center">
                    <div className="text-center mb-4">
                      <UploadIcon />

                      <h6 className="text-[14px] font-Mulish font-normal text-darkgrey mb-3 mt-3">
                        Passport image
                      </h6>
                      <p className="text-[12px] font-Mulish font-normal text-[#98A2B3] w-full sm:w-[80%] mx-auto mb-5">
                        Upload the front side of the document supports: JPG,
                        PNG, PDF
                      </p>
                      <div className="m-auto">
                        <input
                          type="file"
                          accept="image/jpeg, image/png, application/pdf"
                          {...register("passportimage", {
                            required: !visaAndPassportimages?.passportImage
                              ? "Passport image is required"
                              : false,
                          })}
                          onChange={(e) => handleFileChange(e, "front")}
                          ref={frontFileInputRef}
                          hidden
                        />
                        <button
                          type="button"
                          onClick={() => handleFileChooseClick("front")}
                          className="border border-solid border-[#ccc] text-sm font-normal  text-[#585C7B]  rounded-full px-4 py-2"
                        >
                          Choose file
                        </button>
                      </div>
                    </div>
                    {errors.passportimage && (
                      <ErrorsMessage
                        title={errors.passportimage.message}
                        className="absolute top-[100%] left-0"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="w-full sm:w-1/2">
                {visaImageimagePreview ? (
                  <div className="bg-[#EAECF0] p-5 w-full text-center rounded-[20px] relative h-[250px] flex justify-center items-center">
                    {visaImage && visaImage.type === "application/pdf" ? (
                      <div className="text-center">
                        <img
                          src={pdf_icon}
                          alt="pdf_icon "
                          className="rounded-[20px] text-center m-auto"
                        />
                        <p className="font-Mulish text-[#000] font-normal text-base">
                          {visaImage.name.length > 25
                            ? `${visaImage.name.slice(0, 25)}...`
                            : visaImage.name}
                        </p>
                      </div>
                    ) : (
                      <img
                        src={visaImageimagePreview}
                        alt="Uploaded Preview"
                        className="mx-auto h-[220px] rounded-[20px]"
                        onError={(e) => (e.currentTarget.src = pdf_icon)}
                      />
                    )}
                    <DeleteIcon
                      onClick={() => handleDeleteImage("visa")}
                      className="absolute cursor-pointer top-[10px] right-[10px]"
                    />
                  </div>
                ) : (
                  <div className="bg-[#EAECF0] p-5 w-full text-center  rounded-[20px] relative h-[250px] flex justify-center items-center">
                    <div className="text-center mb-4">
                      <UploadIcon />
                      <h6 className="text-[14px] font-Mulish font-normal text-darkgrey mb-3 mt-3">
                        Visa application
                      </h6>
                      <p className="text-[12px] font-Mulish font-normal text-[#98A2B3] w-full sm:w-[80%] mx-auto mb-5">
                        Upload the Back side of the document supports: JPG, PNG,
                        PDF
                      </p>
                      <div className="m-auto">
                        <input
                          type="file"
                          accept="image/jpeg, image/png , application/pdf"
                          {...register("visaImage", {
                            required: !visaAndPassportimages?.visaImage
                              ? "Visa image is required"
                              : false,
                          })}
                          onChange={(e) => handleFileChange(e, "back")}
                          ref={backFileInputRef}
                          hidden
                        />
                        <button
                          type="button"
                          onClick={() => handleFileChooseClick("back")}
                          className="border border-solid border-[#ccc] text-sm font-normal  text-[#585C7B]  rounded-full px-4 py-2"
                        >
                          Choose file
                        </button>
                      </div>
                    </div>
                    {errors.visaImage && (
                      <ErrorsMessage
                        title={errors.visaImage.message}
                        className="absolute top-[100%] left-0"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isLoading}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartEighth;
